import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMessageGetter } from 'react-message-context';
import styled from 'styled-components/macro';
import { FileIcon, QuestionIcon } from '../../../assets';
import { Body2, Button, H4 } from '../../../components/common';
import { DropdownOptionValue } from '../../../components/common/Dropdown';
import {
  FileInput,
  FileInputValue,
  FormActions,
  InputField,
  TextAreaElement,
  TimeDropdown,
} from '../../../components/common/forms';
import { scrollToTop } from '../../../utils';
import { FormPageContainer } from '../FormPageContainer';
import { Column, ContentBlock, Form, FormRow, GridContainer, Row } from '../layoutComponents';
import { H2, H3, TextBlock } from '../Text';
import { InfoPopup } from './InfoPopup';
import { schema, TravelExpenseReportFormState } from './schema';
import { TravelExpenseReportPreview } from './TravelExpenseReportPreview';
import { showNotification } from '../../../context/appActions';
import { ExpenseReportForm, ExpenseReportFormType } from '../../../types/expense-reports';
import { NotificationType, useAppDispatch } from '../../../context/appContext';
import { createExpenseReport, submitExpenseReport, updateExpenseReport } from '../../../apis_kenno';
import Submitted from '../Submitted';
import FormError from '../../../components/common/forms/FormError';
import { getFileDisplayName, objectToFormData } from '../form-utils';

const ContactInfoContent = styled(ContentBlock)``;
const EventContent = styled(ContentBlock)``;
const ExpenseReportsContent = styled(ContentBlock)``;

const OwnCarInfoText = styled.span`
  display: inline-flex;
  text-decoration: underline;
  cursor: pointer;
  gap: 6px;
`;

interface Props {
  updateForm?: ExpenseReportForm;
}

export const TravelExpenseReport: React.FC<Props> = ({ updateForm }) => {
  const appDispatch = useAppDispatch();
  const [isBusy, setIsBusy] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialId = updateForm?.id || null;
  const signedUrls = updateForm?.signedUrls || [];
  const [formId, setFormId] = useState<string | null>(initialId);
  const [submitted, setSubmitted] = useState(false);

  const initialValues = {};

  const formMethods = useForm<TravelExpenseReportFormState>({
    resolver: zodResolver(schema),
    defaultValues: updateForm ? { ...initialValues, ...updateForm.formData } : { ...initialValues },
  });

  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [isOwnCarInfoPopupOpen, setIsOwnCarInfoPopupOpen] = useState<boolean>(false);

  const t = useMessageGetter('forms.travelExpenseReport');
  const tCommon = useMessageGetter('forms');

  const handleSubmitFinal = async (data: TravelExpenseReportFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    // Convert to FormData to support file uploads
    const formData = objectToFormData(data);
    formData.append('formType', ExpenseReportFormType.TravelExpenseReport);

    try {
      let id = formId;
      if (id) {
        await updateExpenseReport(id, formData);
      } else {
        id = (await createExpenseReport(formData)).id;
      }
      await submitExpenseReport(id);
      handleEmptyForm();
      setSubmitted(true);
      scrollToTop();
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.submit'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleSave = async (data: TravelExpenseReportFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    // Convert to FormData to support file uploads
    const formData = objectToFormData(data);
    formData.append('formType', ExpenseReportFormType.TravelExpenseReport);

    try {
      if (!formId) {
        const expenseReport = await createExpenseReport(formData);
        setFormId(expenseReport.id);
      } else {
        await updateExpenseReport(formId, formData);
      }
      await showNotification(appDispatch, tCommon('info.saved'), NotificationType.SUCCESS);
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.save'));
    } finally {
      setIsBusy(false);
    }
  };

  const {
    fields: publicExpenseFields,
    append: publicExpenseAppend,
    remove: publicExpenseRemove,
  } = useFieldArray({
    control: formMethods.control,
    name: 'publicExpenseReports',
  });

  const {
    fields: ownCarExpenseFields,
    append: ownCarExpenseAppend,
    remove: ownCarExpenseRemove,
  } = useFieldArray({
    control: formMethods.control,
    name: 'ownCarExpenseReports',
  });

  const maximumExpensesAdded = publicExpenseFields.length + ownCarExpenseFields.length >= 3;

  const handleAddNewPublicTransportationExpense = () => {
    if (maximumExpensesAdded) {
      return;
    }
    publicExpenseAppend({
      amount: 0.0,
      additionalInformation: '',
      vehicle: '',
      // This typecasting is a workaround for typescript error stating that receipt can't be undefined
      // This works differently than useForm's defaultValues in which the values can be null or undefined.
      // Unfortunately append function is strictly following the schema in which the values are required.
      // Discussion about the flaw here: https://github.com/orgs/react-hook-form/discussions/10211
      date: undefined as unknown as string,
      time: undefined as unknown as string,
      receipts: undefined as unknown as FileList,
    });
  };

  const publicExpenseReports = formMethods.watch('publicExpenseReports');

  const handleAddNewOwnTransportationExpense = () => {
    if (maximumExpensesAdded) {
      return;
    }
    ownCarExpenseAppend({
      additionalInformation: '',
      numberOfPeople: 0,
      totalKm: 0,
      // This typecasting is a workaround for typescript error stating that receipt can't be undefined
      // This works differently than useForm's defaultValues in which the values can be null or undefined.
      // Unfortunately append function is strictly following the schema in which the values are required.
      // Discussion about the flaw here: https://github.com/orgs/react-hook-form/discussions/10211
      date: undefined as unknown as string,
      time: undefined as unknown as string,
    });
  };

  const handleEmptyForm = () => {
    formMethods.reset();
  };

  const handleTogglePreview = () => {
    setPreviewOpen(!previewOpen);
    scrollToTop();
  };

  const handleOnClickOwnCarInfo = () => {
    setIsOwnCarInfoPopupOpen(true);
  };

  const handleCloseOwnCarInfo = () => {
    setIsOwnCarInfoPopupOpen(false);
  };

  const translationPrefix = 'forms.travelExpenseReport';

  return (
    <FormPageContainer<TravelExpenseReportFormState> formMethods={formMethods}>
      {!previewOpen && <H2>{t('title')}</H2>}
      {submitted ? (
        <Submitted message={tCommon('info.submitted')} />
      ) : (
        <Form>
          {previewOpen ? (
            <TravelExpenseReportPreview formData={formMethods.getValues()} signedUrls={signedUrls} />
          ) : (
            <>
              <span>{t('description')}</span>
              <ContactInfoContent>
                <H3>{t('contactInfo.title')}</H3>
                <div>
                  <FormRow>
                    <InputField<TravelExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.name"
                      type="text"
                      required
                    />
                  </FormRow>
                  <InputField<TravelExpenseReportFormState, string>
                    translationPrefix={translationPrefix}
                    fieldPath="contactInfo.address"
                    type="text"
                    required
                  />
                  <FormRow>
                    <InputField<TravelExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.zip"
                      type="text"
                      required
                    />
                    <InputField<TravelExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.city"
                      type="text"
                      required
                    />
                  </FormRow>
                  <FormRow>
                    <InputField<TravelExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.phone"
                      type="text"
                      required
                    />
                    <InputField<TravelExpenseReportFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath="contactInfo.email"
                      type="text"
                      required
                    />
                  </FormRow>
                  <InputField<TravelExpenseReportFormState, string>
                    translationPrefix={translationPrefix}
                    fieldPath="contactInfo.iban"
                    type="text"
                    required
                  />
                </div>
              </ContactInfoContent>
              <EventContent>
                <H3>{t('event.title')}</H3>
                <div>
                  <InputField<TravelExpenseReportFormState, string>
                    translationPrefix={translationPrefix}
                    fieldPath="event.name"
                    type="text"
                    required
                  />
                  <InputField<TravelExpenseReportFormState, string>
                    translationPrefix={translationPrefix}
                    fieldPath="event.location"
                    type="text"
                    required
                  />
                  <InputField<TravelExpenseReportFormState, string>
                    translationPrefix={translationPrefix}
                    fieldPath="event.travelRoute"
                    type="text"
                    required
                  />
                </div>
              </EventContent>
              <ExpenseReportsContent>
                <H3>{t('reports.title')}</H3>
                <span>{t('reports.description')}</span>
                <div>
                  <H4>{t('reports.publicTransportationTitle')}</H4>
                  {publicExpenseFields.map((field, index) => {
                    const fileName = getFileDisplayName(publicExpenseReports?.[index]?.receipts?.[0]);
                    return (
                      <div key={field.id} style={{ marginBottom: '24px' }}>
                        <GridContainer columnGap="20px" columns={2} mdColumns={1}>
                          <InputField<TravelExpenseReportFormState, string>
                            fieldPath={`publicExpenseReports.${index}.date`}
                            translationPrefix={translationPrefix}
                            type="date"
                            required
                          />
                          <InputField<TravelExpenseReportFormState, DropdownOptionValue>
                            translationPrefix={translationPrefix}
                            fieldPath={`publicExpenseReports.${index}.time`}
                            inputElementRenderer={(props) => <TimeDropdown {...props} />}
                          />
                        </GridContainer>
                        <GridContainer columnGap="20px" columns={5} mdColumns={1}>
                          <Column span={4} mdSpan={1}>
                            <InputField<TravelExpenseReportFormState, string>
                              translationPrefix={translationPrefix}
                              fieldPath={`publicExpenseReports.${index}.vehicle`}
                              type="text"
                              required
                            />
                          </Column>
                          <Column>
                            <InputField<TravelExpenseReportFormState, string>
                              translationPrefix={translationPrefix}
                              fieldPath={`publicExpenseReports.${index}.amount`}
                              required
                              type="number"
                              step="0.01"
                            />
                          </Column>
                        </GridContainer>
                        <FormRow>
                          <InputField<TravelExpenseReportFormState, FileInputValue>
                            translationPrefix={translationPrefix}
                            hideLabel
                            fieldPath={`publicExpenseReports.${index}.receipts`}
                            inputElementRenderer={(props) => (
                              <FormRow>
                                <FileInput onChange={props.onChange} label={t('addReceipt')} />
                                {fileName && (
                                  <Row style={{ alignItems: 'center' }}>
                                    <FileIcon style={{ flexShrink: 0 }} />
                                    <TextBlock>{fileName}</TextBlock>
                                  </Row>
                                )}
                              </FormRow>
                            )}
                          />
                        </FormRow>
                        <FormRow style={{ marginBottom: '10px' }}>{t('publicExpenseReports.receiptInfo')}</FormRow>
                        <InputField<TravelExpenseReportFormState, string>
                          translationPrefix={translationPrefix}
                          fieldPath={`publicExpenseReports.${index}.additionalInformation`}
                          inputElementRenderer={(props) => <TextAreaElement {...props} />}
                        />

                        {!!publicExpenseFields.length && (
                          <Button primary onClick={() => publicExpenseRemove(index)}>
                            {t('reports.remove')}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <FormRow style={{ alignItems: 'center', position: 'relative' }}>
                  <Button
                    type="button"
                    icon="plus"
                    disabled={maximumExpensesAdded}
                    onClick={handleAddNewPublicTransportationExpense}
                  >
                    {t('reports.addPublicTransportationExpense')}
                  </Button>
                  {maximumExpensesAdded && <Body2>{t('reports.limitReached')}</Body2>}
                </FormRow>

                <H4>{t('reports.ownTransportationTitle')}</H4>
                {ownCarExpenseFields.map((field, index) => {
                  return (
                    <div key={field.id} style={{ marginBottom: '24px' }}>
                      <GridContainer columnGap="20px" columns={2} mdColumns={1}>
                        <InputField<TravelExpenseReportFormState, string>
                          fieldPath={`ownCarExpenseReports.${index}.date`}
                          translationPrefix={translationPrefix}
                          type="date"
                          required
                        />
                        <InputField<TravelExpenseReportFormState, DropdownOptionValue>
                          translationPrefix={translationPrefix}
                          fieldPath={`ownCarExpenseReports.${index}.time`}
                          inputElementRenderer={(props) => <TimeDropdown {...props} />}
                        />
                      </GridContainer>
                      <GridContainer columnGap="20px" columns={3} mdColumns={3}>
                        <Column>
                          <InputField<TravelExpenseReportFormState, string>
                            translationPrefix={translationPrefix}
                            fieldPath={`ownCarExpenseReports.${index}.numberOfPeople`}
                            type="number"
                            required
                          />
                        </Column>
                        <Column>
                          <InputField<TravelExpenseReportFormState, string>
                            translationPrefix={translationPrefix}
                            fieldPath={`ownCarExpenseReports.${index}.totalKm`}
                            required
                            type="number"
                            step="0.01"
                          />
                        </Column>
                      </GridContainer>
                      <FormRow style={{ marginBottom: '10px' }}>
                        <span>
                          {t('ownCarExpenseReports.compensationInfo')}{' '}
                          <a href="https://vero.fi/henkiloasiakkaat/vahennykset/kilometrikorvaus-ja-paivaraha/">
                            vero.fi
                          </a>
                          .
                        </span>
                      </FormRow>
                      <InputField<TravelExpenseReportFormState, string>
                        translationPrefix={translationPrefix}
                        fieldPath={`ownCarExpenseReports.${index}.additionalInformation`}
                        inputElementRenderer={(props) => <TextAreaElement {...props} />}
                      />
                      <Button primary onClick={() => ownCarExpenseRemove(index)}>
                        {t('reports.remove')}
                      </Button>
                    </div>
                  );
                })}

                <FormRow style={{ alignItems: 'center', position: 'relative' }}>
                  <Button
                    type="button"
                    icon="plus"
                    onClick={handleAddNewOwnTransportationExpense}
                    disabled={maximumExpensesAdded}
                  >
                    {t('reports.addOwnTransportationExpense')}
                  </Button>
                  {maximumExpensesAdded && <Body2>{t('reports.limitReached')}</Body2>}

                  <OwnCarInfoText onClick={handleOnClickOwnCarInfo}>
                    <QuestionIcon />
                    {t('info')}
                  </OwnCarInfoText>
                  {isOwnCarInfoPopupOpen && (
                    <InfoPopup
                      subject={t('ownCarInfoPopup.subject')}
                      description={t('ownCarInfoPopup.description')}
                      onClose={handleCloseOwnCarInfo}
                      visible={isOwnCarInfoPopupOpen}
                      width="403px"
                      bottom="55px"
                      right="0px"
                    />
                  )}
                </FormRow>
              </ExpenseReportsContent>
            </>
          )}
          {submitFailed && <FormError message={errorMessage} />}
          <FormActions
            handleEmptyForm={handleEmptyForm}
            handleTogglePreview={handleTogglePreview}
            handleSave={formMethods.handleSubmit(handleSave)}
            handleSubmitFinal={formMethods.handleSubmit(handleSubmitFinal)}
            actionButtonType="send"
            previewOpen={previewOpen}
            disabled={isBusy}
          />
        </Form>
      )}
    </FormPageContainer>
  );
};
